import { useCallback, useEffect, useRef } from 'react';

/**
 * Delay before a hover annotation opens — long enough that skimming a dense table
 * row-by-row does not flash a card on every row, short enough to still feel
 * immediate when the pointer rests. Shared default so every lens annotation feels
 * the same (L14).
 */
export const HOVER_INTENT_DELAY_MS = 300;

/**
 * Hover-INTENT: fire `onIntent` only after the pointer has rested for `delayMs`,
 * and cancel the moment it leaves, re-enters another target, or the component
 * unmounts. The ONE place the cash-flow delayed-hover timing lives, so the
 * register's settlement hover and any other lens annotation share identical
 * behaviour (L14) instead of each re-implementing a timer. Generic over the
 * payload the caller wants delivered on intent (e.g. the hovered entry id + the
 * cursor position).
 */
export function useHoverIntent<T>(
  onIntent: (payload: T) => void,
  delayMs: number = HOVER_INTENT_DELAY_MS,
): { start: (payload: T) => void; cancel: () => void } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(
    (payload: T) => {
      cancel();
      timer.current = setTimeout(() => {
        timer.current = null;
        onIntent(payload);
      }, delayMs);
    },
    [cancel, delayMs, onIntent],
  );

  useEffect(() => cancel, [cancel]);

  return { start, cancel };
}
